/**
 * Offline payment instructions — terminal screen for the "Pay offline"
 * choice on Confirm.
 *
 * The registration is already created and reserved (status pending_payment)
 * with an offline memo; nothing is charged here. The entrant sends the money
 * out of band (Venmo / Cash App / cash / check) with the memo code, and the
 * organizer confirms receipt from the admin portal ("Mark paid"), which
 * flips the registration to confirmed and emails the receipt. All the
 * offline detail (handles, memo, amount) rides on the registration object
 * stashed in FlowContext by the Confirm page.
 */
import { useParams } from 'react-router-dom';

import type { ConfigResponse, EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { useVocab } from '@/state/vocab';
import type { DisplayMode } from '@/App';
import './OfflinePayPage.css';

interface Props {
  // Present for prop-shape uniformity with the other terminal screens.
  client?: EmbedClient;
  config: ConfigResponse | null;
  displayMode: DisplayMode;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function OfflinePayPage({ config, displayMode }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();
  const { registration } = useFlow();
  const vocab = useVocab();

  // Landed here without an offline registration (e.g. reload lost the
  // in-memory flow state) — bounce back to the start.
  if (!registration || registration.payment_method !== 'offline') {
    navigate(`/events/${eventId}`, { replace: true });
    return null;
  }

  const memo = registration.offline_memo_code;
  const venmo = registration.offline_venmo;
  const cashapp = registration.offline_cashapp;
  const amount = registration.offline_amount_cents ?? null;
  const organizer = config?.branding.organizer_name ?? 'The organizer';

  const copy = (text: string) => {
    try { void navigator.clipboard?.writeText(text); } catch { /* noop */ }
  };

  // Deep link that opens Venmo (app on mobile, web on desktop) with the payee,
  // amount, and memo pre-filled so the entrant just has to hit send.
  const venmoUrl = venmo
    ? 'https://venmo.com/?' + new URLSearchParams({
        txn: 'pay',
        audience: 'private',
        recipients: venmo,
        ...(amount ? { amount: (amount / 100).toFixed(2) } : {}),
        ...(memo ? { note: memo } : {}),
      }).toString()
    : null;

  // Opened as a script popup window (the Wix "popup window" integration uses
  // window.open). There, "Back to organizer" should CLOSE the popup rather
  // than navigate it to the return URL. Mirrors the Done screens.
  const isPopupWindow =
    typeof window !== 'undefined' && !!window.opener && window.opener !== window;

  return (
    <div className="tn-offline tn-card">
      <div className="tn-offline__icon" aria-hidden>💸</div>
      <h1 className="tn-offline__title">One more step — send your payment</h1>
      <p className="tn-offline__subtitle">
        Your {vocab.entrant} is reserved. Send{amount ? ` ${money(amount)}` : ' your payment'} using
        one of the options below, and include the memo code so {organizer} can match it to your
        registration.
      </p>

      {amount ? <div className="tn-offline__amount">{money(amount)}</div> : null}

      <div className="tn-offline__handles">
        {venmo ? (
          <>
            <button type="button" className="tn-offline__handle" onClick={() => copy(`@${venmo}`)}>
              <span className="tn-offline__handle-label">Venmo</span>
              <span className="tn-offline__handle-value">@{venmo}</span>
              <span className="tn-offline__handle-copy" aria-hidden>Copy</span>
            </button>
            {venmoUrl ? (
              <a
                className="tn-btn tn-btn--primary tn-offline__venmo-open"
                href={venmoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Venmo{amount ? ` — ${money(amount)}` : ''}
              </a>
            ) : null}
          </>
        ) : null}
        {cashapp ? (
          <button type="button" className="tn-offline__handle" onClick={() => copy(`$${cashapp}`)}>
            <span className="tn-offline__handle-label">Cash App</span>
            <span className="tn-offline__handle-value">${cashapp}</span>
            <span className="tn-offline__handle-copy" aria-hidden>Copy</span>
          </button>
        ) : null}
        {!venmo && !cashapp ? (
          <p className="tn-offline__cash">
            Pay {organizer} by cash or check and reference the memo code below.
          </p>
        ) : null}
      </div>

      {memo ? (
        <div className="tn-offline__memo">
          <span className="tn-offline__memo-label">Payment memo</span>
          <button type="button" className="tn-offline__memo-code" onClick={() => copy(memo)}>
            {memo}
          </button>
          <span className="tn-offline__memo-hint">
            Put this in the payment note so your {vocab.entrant} gets credited.
          </span>
        </div>
      ) : null}

      <p className="tn-offline__foot">
        {organizer} will confirm once your payment arrives, and we'll email your receipt.
        Your spot is held until then.
      </p>

      {isPopupWindow ? (
        <div className="tn-offline__actions">
          <button className="tn-btn tn-btn--secondary" type="button" onClick={() => window.close()}>
            {config?.branding.organizer_name ? `Back to ${config.branding.organizer_name}` : 'Close'}
          </button>
        </div>
      ) : displayMode === 'redirect' && config?.return_url ? (
        <div className="tn-offline__actions">
          <a className="tn-btn tn-btn--secondary" href={config.return_url}>
            Back to {config.branding.organizer_name}
          </a>
        </div>
      ) : null}
    </div>
  );
}
